<template>
  <v-card>
    <v-card-title class="grey--text text-subtitle-1">
      Crisantemi
      <v-btn icon small class="ml-2" :loading="caricamento" @click="carica">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <v-select
        v-model="anno"
        :items="anni"
        label="Anno"
        outlined
        dense
        hide-details
        class="ml-6"
        style="max-width: 120px"
      />

      <v-spacer />
      <IndicatoreSalvataggio class="mr-4" />

      <!-- L'activator lo fornisce la lista: la classe deve atterrare sul
           bottone, non sul contenitore che v-dialog rende come radice. -->
      <CalendarioConsegne :anno="anno" @apri="apri">
        <template v-slot:activator="{ on }">
          <v-btn outlined color="amber darken-2" class="mr-5" v-on="on">
            <v-icon left>mdi-calendar</v-icon>Calendario
          </v-btn>
        </template>
      </CalendarioConsegne>

      <NuovaPrenotazione @apri="apri_creata" />
    </v-card-title>

    <v-tabs v-model="scheda" height="40">
      <v-tab>Prenotazioni</v-tab>
      <v-tab>
        Disponibilità
        <v-chip v-if="scoperti" x-small color="red lighten-4" class="ml-2">
          {{ scoperti }}
        </v-chip>
      </v-tab>
    </v-tabs>
    <v-divider />

    <v-card-text>
      <v-tabs-items v-model="scheda">
        <v-tab-item>
          <ListaPrenotazioni
            :anno="anno"
            :caricamento="caricamento"
            @apri="apri"
            @errore="segnala"
          />
        </v-tab-item>

        <v-tab-item>
          <!-- Montata solo all'apertura: carica i colori di tutte le specie
               coinvolte, che non serve fare finché resta chiusa. -->
          <GestioneDisponibilita
            v-if="scheda === 1"
            ref="disponibilita"
            :anno="anno"
            @apri="apri_da_disponibilita"
          />
        </v-tab-item>
      </v-tabs-items>

      <PrenotazioneDettaglio
        ref="dettaglio"
        :id-prenotazione="id_aperta"
        :voce-evidenziata="voce_evidenziata"
        :prodotto-evidenziato="prodotto_evidenziato"
      />
    </v-card-text>

    <v-snackbar v-model="avviso" color="red" text outlined top timeout="5000">
      <v-icon color="red" class="mr-2">mdi-alert</v-icon>{{ messaggio }}
    </v-snackbar>
  </v-card>
</template>

<script>
import moment from "moment";
import NuovaPrenotazione from "./NuovaPrenotazione.vue";
import ListaPrenotazioni from "./ListaPrenotazioni.vue";
import GestioneDisponibilita from "./GestioneDisponibilita.vue";
import PrenotazioneDettaglio from "./PrenotazioneDettaglio.vue";
import IndicatoreSalvataggio from "./IndicatoreSalvataggio.vue";
import CalendarioConsegne from "./CalendarioConsegne.vue";

/**
 * Orchestratore del modulo crisantemi.
 *
 * Possiede l'anno di riferimento — condiviso da lista, disponibilità e
 * calendario — e il dettaglio, che si apre da tutti e tre.
 */
export default {
  components: {
    NuovaPrenotazione,
    ListaPrenotazioni,
    GestioneDisponibilita,
    PrenotazioneDettaglio,
    IndicatoreSalvataggio,
    CalendarioConsegne,
  },

  data() {
    return {
      anno: moment().format("YYYY"),
      scheda: 0,
      caricamento: false,
      id_aperta: null,
      voce_evidenziata: null,
      prodotto_evidenziato: null,
      sospendi_ricarica: false,
      avviso: false,
      messaggio: "",
    };
  },

  computed: {
    anni() {
      const adesso = Number(moment().format("YYYY"));
      return [...Array(8).keys()].map((i) => String(adesso + 1 - i));
    },

    /** Conteggio degli scoperti, per il segnalino sulla scheda. */
    scoperti() {
      return this.$store.getters["crisantemi/scoperti"].length;
    },
  },

  watch: {
    anno() {
      if (this.sospendi_ricarica) return;
      this.carica();
    },
  },

  mounted() {
    this.carica();
  },

  methods: {
    async carica(id_da_aprire = null) {
      this.caricamento = true;
      try {
        // La disponibilità serve anche alla lista, per il segnalino degli
        // scoperti sulla scheda: si carica senza aspettare che si apra.
        await Promise.all([
          this.$store.dispatch("crisantemi/fetch_prenotazioni", this.anno),
          this.$store.dispatch("crisantemi/fetch_dispo", this.anno),
        ]);
        if (id_da_aprire) this.id_aperta = id_da_aprire;
      } catch (err) {
        this.segnala({ cosa: "Caricamento non riuscito", err });
      } finally {
        this.caricamento = false;
      }
    },

    apri(id) {
      this.voce_evidenziata = null;
      this.prodotto_evidenziato = null;
      this.id_aperta = id;
      this.$nextTick(() => this.$refs.dettaglio.apri());
    },

    apri_da_disponibilita({ id, colore, prodotto }) {
      this.id_aperta = id;
      this.voce_evidenziata = colore;
      this.prodotto_evidenziato = prodotto;
      this.$nextTick(() => this.$refs.dettaglio.apri());
    },

    // La prenotazione creata può appartenere a un anno diverso da quello a video.
    async apri_creata(prenotazione) {
      if (prenotazione.anno !== this.anno) {
        this.sospendi_ricarica = true;
        this.anno = prenotazione.anno;
        await this.$nextTick();
        this.sospendi_ricarica = false;
      }
      this.scheda = 0;
      await this.carica(prenotazione.id);
      this.$nextTick(() => this.$refs.dettaglio.apri());
    },

    segnala({ cosa, err }) {
      const stato = err && err.response ? err.response.status : null;
      this.messaggio = stato
        ? `${cosa}: errore ${stato}.`
        : `${cosa}: server non raggiungibile.`;
      this.avviso = true;
    },
  },
};
</script>
